import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useMemo, useRef, useState} from 'react';
import {Box, styled} from '@mui/material';
import {
  Body,
  Button,
  Checkbox,
  designSystemComposition,
  designSystemToken,
  Icon,
  Input,
  Tooltip,
} from '@lightricks/react-design-system';
import ActionsMenu from '@contentlab/components/actions-menu';
import {MenuOption} from '@contentlab/components/actions-menu/ActionsMenu';
import ThumbnailDisplay from '@contentlab/components/thumbnail-display';
import {UploadingFile} from '@contentlab/stores/contentLabStore';
import {AssetMetadata} from '@contentlab/types/asset';
import {FeatureName, FlowStatus} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import {
  getMetadataDisplayValue,
  MetadataDisplayKey,
} from '@contentlab/utils/featureHelpers';
import translate from '@/utils/translate';
import styles from './AssetCard.module.scss';
import AssetProgressBar from './components/asset-progress-bar';
import AssetUploadProgressBar from './components/asset-upload-progress-bar';
import StatusBadge from './components/status-badge';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

const THUMBNAIL_SIZE = 90;

export enum ViewMode {
  Upload = 'Upload',
  InProcess = 'InProcess',
  Ready = 'Ready',
  Failed = 'Failed',
  Done = 'Done',
}

enum RenderField {
  FileName = 'fileName',
  Metadata = 'metadata',
  Status = 'status',
  UploadProgress = 'uploadProgress',
  ProcessProgress = 'processProgress',
  Actions = 'actions',
  Dummy = 'dummy',
}

enum Action {
  Download = 'download',
  Rename = 'rename',
  Delete = 'delete',
}

interface ViewModeConfig {
  renderFields: RenderField[];
  actions?: string[];
  thumbnailIcon?: React.ReactNode;
}

const DropShadowIcon = styled(Icon)(() => ({
  filter:
    'drop-shadow(0px 0px 16px rgba(0, 0, 0, 0.24)) drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04))',
}));

function PlayIcon() {
  return (
    <DropShadowIcon
      size="medium"
      appearance="neutral"
      name="StateBar-Play"
      color={designSystemToken('semantic.fg.white')}
    />
  );
}

function PauseIcon() {
  return (
    <DropShadowIcon
      size="medium"
      appearance="neutral"
      name="Actions-Pause"
      color={designSystemToken('semantic.fg.white')}
    />
  );
}

function PlayStatusIcon({isPlaying}: {isPlaying: boolean}) {
  return isPlaying ? <PauseIcon /> : <PlayIcon />;
}

const VIEW_MODE_MAP: (
  asset: AssetProps | undefined,
  isPlaying?: boolean
) => Record<ViewMode, ViewModeConfig> = (
  asset?: AssetProps,
  isPlaying = false
) => ({
  [ViewMode.Upload]: {
    renderFields: [
      RenderField.FileName,
      RenderField.Metadata,
      RenderField.UploadProgress,
    ],
    thumbnailIcon:
      asset?.status === 'ready' ? (
        <PlayStatusIcon isPlaying={isPlaying} />
      ) : undefined,
  },
  [ViewMode.InProcess]: {
    renderFields: [
      RenderField.FileName,
      RenderField.Metadata,
      RenderField.ProcessProgress,
    ],
  },
  [ViewMode.Ready]: {
    renderFields: [
      RenderField.FileName,
      RenderField.Metadata,
      RenderField.Status,
      RenderField.Actions,
    ],
    actions: [Action.Download, Action.Rename, Action.Delete],
    thumbnailIcon: <PlayStatusIcon isPlaying={isPlaying} />,
  },
  [ViewMode.Done]: {
    renderFields: [
      RenderField.FileName,
      RenderField.Metadata,
      RenderField.Status,
      RenderField.Actions,
    ],
    actions: [Action.Download, Action.Rename, Action.Delete],
    thumbnailIcon: <PlayStatusIcon isPlaying={isPlaying} />,
  },
  [ViewMode.Failed]: {
    renderFields: [
      RenderField.FileName,
      RenderField.Metadata,
      RenderField.Status,
      RenderField.Dummy,
    ],
    thumbnailIcon: (
      <Icon
        name="Actions-Patch-Cancel-Fill"
        size="medium"
        appearance="neutral"
        color={designSystemToken('semantic.fg.secondary')}
      />
    ),
  },
});

export interface AssetCardProps {
  testID?: string;
  asset?: AssetProps;
  file?: UploadingFile;
  selected?: boolean;
  checked?: boolean;
  isPlaying?: boolean;
  onCheck?: (selected: boolean) => void;
  onClick?: (asset: AssetProps) => void;
  onThumbnailClick?: (asset: AssetProps) => void;
  thumbnailIcon?: React.ReactNode;
  displayThumbnailIcon?: boolean;
  showCheckbox?: boolean;
  metadataDisplayParam?: MetadataDisplayKey;
  viewMode?: ViewMode;
  renameAsset?: ({
    assetId,
    name,
  }: {
    assetId: string;
    name: string;
  }) => Promise<void>;
  deleteAsset?: (asset: AssetProps) => Promise<void>;
  downloadAsset?: (asset: AssetProps) => Promise<void>;
  onActionsClick?: (assetId: string) => void;
}

const shouldNotForwardProps = ['selected', 'isFailed', 'onCheck'];

const Container = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(
  ({
    selected,
    isFailed,
    onClick,
  }: {
    selected: boolean;
    isFailed: boolean;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  }) => ({
    position: 'relative',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: '16px',
    borderRadius: '12px',
    paddingInlineEnd: '16px',
    overflow: 'hidden',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: (() => {
      if (isFailed) {
        return designSystemToken('semantic.bg.danger-secondary');
      }
      if (selected) {
        return designSystemToken('semantic.fg.primary');
      }
      return designSystemToken('semantic.bg.neutral-secondary');
    })(),
    background: selected
      ? designSystemToken('semantic.bg.tertiary')
      : designSystemToken('semantic.bg.primary'),
    cursor: !isFailed && onClick ? 'pointer' : 'default',
  })
);

const ThumbnailContainer = styled(Box)(() => ({
  'position': 'relative',
  'flex': '0 0 80px',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  '.MuiAvatar-root': {
    borderRadius: 0,
    background: designSystemToken('semantic.bg.alpha'),
  },
}));

const AssetContentContainer = styled(Box)(() => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  minWidth: 0,
}));

const NameCell = styled(Box)(() => ({
  flex: '2 1 0',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
}));

const MetadataCell = styled(Box)(() => ({
  flex: '2 0 0',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StatusCell = styled(Box)(() => ({
  flex: '1 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

const ActionsCell = styled(Box)(() => ({
  flex: '1 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  cursor: 'pointer',
}));

const DummyCell = styled(Box)(() => ({
  flex: '1 0 0',
  display: 'flex',
}));

const CheckboxContainer = styled(Box)(() => ({
  position: 'absolute',
  left: '8px',
  top: '8px',
  zIndex: 1,
  width: '18px',
  height: '18px',
  overflow: 'hidden',
  borderRadius: '4px',
}));

const ThumbnailIconContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
}));

const ThumbnailOverlay = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'selected',
})(({selected}: {selected: boolean}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: selected ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  pointerEvents: 'none',
  zIndex: 0,
}));

const StyledDownloadButton = styled(Button)(() => ({
  marginRight: '16px',
  marginLeft: '16px',
  minWidth: 'auto',
  padding: '6px',
  width: '32px',
  height: '32px',
}));

interface DownloadButtonProps {
  asset?: AssetProps;
  onClick: () => Promise<void>;
}

function DownloadButton({asset, onClick}: DownloadButtonProps) {
  const getAssetDownloadTooltip = () => {
    if (asset?.feature === FeatureName.CAPTIONS) {
      return translate('SRT + Burned-in video (ZIP)');
    }
    return '';
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClick();
  };

  return (
    <Tooltip title={getAssetDownloadTooltip()} placement="top" arrow>
      <span>
        <StyledDownloadButton
          onClick={handleClick}
          appearance="neutral"
          mode="plain"
          size="small"
          icon={
            <Icon
              size="medium"
              appearance="neutral"
              name="Navigation-Download"
            />
          }
        />
      </span>
    </Tooltip>
  );
}

function AssetCard({
  testID = 'asset-card',
  asset,
  file,
  selected = false,
  checked = false,
  isPlaying = false,
  onCheck,
  onClick,
  onThumbnailClick,
  thumbnailIcon,
  displayThumbnailIcon = true,
  showCheckbox,
  metadataDisplayParam,
  viewMode = ViewMode.Done,
  renameAsset,
  deleteAsset,
  downloadAsset,
  onActionsClick,
}: AssetCardProps) {
  const [fileNameEditMode, setFileNameEditMode] = useState(false);

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const fileNameInputRef = useRef<HTMLInputElement | null>(null);
  const flags = useFlags();

  const handleOnActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onActionsClick?.(asset?.id || '');
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleOnActionsClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const viewModeConfig = useMemo(() => {
    return VIEW_MODE_MAP(asset, isPlaying)[viewMode];
  }, [viewMode, asset, isPlaying]);

  const thumbnailIconElement =
    displayThumbnailIcon && (thumbnailIcon || viewModeConfig.thumbnailIcon);

  const isLoadingAsset = useMemo(() => {
    if (viewMode === ViewMode.InProcess) {
      return true;
    }
    if (viewMode === ViewMode.Upload && asset?.uploadProgress !== undefined) {
      return asset.uploadProgress < 100;
    }

    return false;
  }, [viewMode, asset]);

  const downloadAssetAction = downloadAsset
    ? async () => {
        if (asset) {
          await downloadAsset(asset);
        }
      }
    : undefined;

  const actions = {
    [Action.Download]: {
      id: 'download',
      title: translate('Download'),
      icon: 'Navigation-Download',
      onClick: downloadAssetAction,
    },
    [Action.Rename]: {
      id: 'rename',
      title: translate('Rename'),
      icon: 'Navigation-Edit',
      onClick: () => {
        if (!asset) {
          return;
        }
        setFileNameEditMode(true);
        setTimeout(() => {
          fileNameInputRef.current?.focus?.();
          fileNameInputRef.current?.select?.();
        });
      },
    },
    [Action.Delete]: {
      id: 'delete',
      title: translate('Delete'),
      icon: 'Actions-Remove',
      onClick: () => {
        if (!asset) {
          return;
        }
        deleteAsset?.(asset);
      },
    },
  } as Record<string, MenuOption>;

  const handleSave = async (value: string) => {
    if (!asset) {
      return;
    }
    const previousName = asset.name;
    setFileNameEditMode(false);
    if (value && value !== previousName) {
      await renameAsset?.({
        assetId: asset.id,
        name: value,
      });
    }
  };

  const renderers = {
    [RenderField.FileName]: () => (
      <NameCell key="fileName">
        {fileNameEditMode ? (
          <Input
            className={styles.overrideInputContainer}
            value={asset?.name || ''}
            clearIcon={false}
            onKeyDown={async (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const {value} = event.target as HTMLInputElement;
                await handleSave(value);
              }
            }}
            inputProps={{
              ref: fileNameInputRef,
              onBlur: async ({
                target: {value},
              }: React.FocusEvent<HTMLInputElement>) => {
                await handleSave(value);
              },
            }}
            sx={{
              'input': {
                ...designSystemComposition('typography.body.md'),
              },
              '.MuiInputBase-root': {
                'border': 'unset !important',
                'paddingInlineStart': 0,
                '.MuiInput-input': {
                  ...designSystemComposition('typography.body.md'),
                },
              },
            }}
          />
        ) : (
          <Body
            size="md"
            color={designSystemToken('semantic.fg.primary')}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {asset?.name || '-'}
          </Body>
        )}
      </NameCell>
    ),
    [RenderField.Metadata]: () => (
      <MetadataCell key="metadata">
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {asset
            ? getMetadataDisplayValue(asset.metadata, metadataDisplayParam, {
                status: asset.status,
                userInput: asset.userInput,
                assetType: asset.type,
              })
            : '-'}
        </Body>
      </MetadataCell>
    ),
    [RenderField.Status]: () => (
      <StatusCell key="status">
        <StatusBadge
          status={asset?.status || FlowStatus.PENDING}
          errors={asset?.errors}
        />
      </StatusCell>
    ),
    [RenderField.UploadProgress]: () => (
      <StatusCell key="uploadProgress" sx={{flex: '2 0 0'}}>
        <AssetUploadProgressBar
          progress={asset?.uploadProgress as number}
          ready={asset?.status === 'ready'}
          errors={asset?.errors}
        />
      </StatusCell>
    ),
    [RenderField.ProcessProgress]: () => (
      <StatusCell key="processProgress" sx={{flex: '2 0 0'}}>
        <AssetProgressBar
          createdAt={asset?.createdAt || new Date()}
          estimatedCompletionTime={asset?.estimatedCompletionTime || null}
        />
      </StatusCell>
    ),
    [RenderField.Actions]: () => {
      const baseOptions = (viewModeConfig.actions || []).map(
        (action) => actions[action as keyof typeof actions] || {}
      );

      const filteredOptions = baseOptions.filter(
        (option) => option.id !== Action.Download
      );

      return (
        <ActionsCell key="actions">
          {downloadAssetAction && (
            <DownloadButton asset={asset} onClick={downloadAssetAction} />
          )}
          <Button
            testID={`${testID}--button-more-actions`}
            onClick={handleOnActionsClick}
            appearance="neutral"
            mode="plain"
            size="small"
            icon={
              <Icon size="medium" appearance="neutral" name="Actions-More" />
            }
          />
          <ActionsMenu
            menuOpen={!!actionsMenuAnchorEl}
            onCloseMenu={(event) => {
              event.stopPropagation();
              handleOnActionsClose();
            }}
            anchorEl={actionsMenuAnchorEl}
            menuOptions={filteredOptions}
          />
        </ActionsCell>
      );
    },
    [RenderField.Dummy]: () => <DummyCell />,
  };

  return (
    <Container
      data-testid={testID}
      selected={selected}
      isFailed={viewMode === ViewMode.Failed || !!asset?.errors?.length}
      onClick={(event) => {
        event.stopPropagation();
        if (viewMode === ViewMode.Failed || !onClick || !asset) {
          return;
        }
        onClick?.(asset);
      }}
    >
      <ThumbnailContainer
        onClick={(event) => {
          if (!onThumbnailClick || !asset) {
            return;
          }
          event.stopPropagation();
          onThumbnailClick?.(asset);
        }}
      >
        {[ViewMode.Ready, ViewMode.Done].includes(viewMode) && showCheckbox && (
          <CheckboxContainer>
            <Checkbox
              style={{transform: 'translate(-2px, -3px)'}}
              checked={checked}
              onChange={() => {}}
              onClick={(event: React.ChangeEvent<HTMLInputElement>) => {
                event.stopPropagation();
                onCheck?.(event.target.checked);
              }}
              customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
              customUncheckedIcon={
                <img
                  src={checkboxUncheckedSrc}
                  style={{
                    background: designSystemToken('semantic.bg.primary'),
                    overflow: 'hidden',
                  }}
                  alt=""
                />
              }
            />
          </CheckboxContainer>
        )}
        <ThumbnailDisplay
          file={file}
          thumbnail={
            viewMode !== ViewMode.Failed ? asset?.thumbnailUrl : undefined
          }
          isLoading={isLoadingAsset}
          loadingType="shimmer"
          width={THUMBNAIL_SIZE}
          height={THUMBNAIL_SIZE}
        />
        <ThumbnailOverlay selected={selected} />
        {thumbnailIconElement ? (
          <ThumbnailIconContainer>
            {thumbnailIconElement}
          </ThumbnailIconContainer>
        ) : null}
      </ThumbnailContainer>
      <AssetContentContainer className="asset-content-container">
        {viewModeConfig.renderFields.map((fieldName) => (
          <React.Fragment key={fieldName}>
            {renderers[fieldName as keyof typeof renderers]()}
          </React.Fragment>
        ))}
      </AssetContentContainer>
    </Container>
  );
}

export default AssetCard;
